import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { faNewspaper } from "@fortawesome/free-solid-svg-icons";

import CustomForm from "../general/form/CustomForm";
import CustomFormSkeleton from "../general/auxiliar/skeleton/CustomFormSkeleton";
import useFormWithDisable from "../../auxiliar/customHooks/useFormWithDisable";
import EmptyTable from "../general/auxiliar/table/EmptyTable";
import { getPatientHistory, putPatientHistory } from "../../actions/patient";
import { fetchData } from "../../auxiliar/auxFunctions";
import { isDefined } from "../../auxiliar/formatValidators";

const PatientHistory = () => {
    const modalId = "delete-patient-modal";
    const {
        register,
        handleSubmit,
        handleSubmitWithoutValidation,
        setValue,
        errors,
        isSubmitting,
    } = useFormWithDisable(false, modalId);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { patient_id: patientId } = useParams();
    const { activeClinic } = useSelector((state) => state.activeClinic);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);

    const [formConfig, setFormConfig] = useState({});
    const [defaultValues, setDefaultValues] = useState({});

    const cancelEditing = () => {
        setIsEditing(false);
        Object.entries(defaultValues).forEach(([key, value]) => {
            setValue(key, value);
        });
    };

    useEffect(() => {
        if (patientId) {
            fetchData(dispatch, getPatientHistory, patientId).then((res) => {
                const historyFieldsConfig = res.content.history_fields_config;
                const parsed = {};

                Object.entries(historyFieldsConfig).forEach(([key, value]) => {
                    const { defaultValue, ...configWithoutDefault } = value;
                    parsed[key] = {
                        ...configWithoutDefault,
                        disabled: !isEditing,
                        name: `${key}-name`,
                        // Only include defaultValue if we don't have patient data for this field
                        ...(res.content.history_fields[key] === undefined && { defaultValue }),
                        ...(value.required
                            ? {
                                  validators: {
                                      required: "Este campo es obligatorio",
                                      ...(value.type === "selector"
                                          ? {
                                                validate: (value) =>
                                                    value !== "select" ||
                                                    "Debe seleccionar una opción",
                                            }
                                          : {}),
                                  },
                              }
                            : {}),
                    };
                });
                // order dictionary by position
                const ordered = Object.fromEntries(
                    Object.entries(parsed).sort((a, b) => a[1].position - b[1].position),
                );

                const values = res.content.history_fields;

                // Prepare form values with proper boolean conversion
                const formValues = {};
                for (const [key, value] of Object.entries(values)) {
                    const fieldConfig = historyFieldsConfig[key];
                    if (!isDefined(fieldConfig) || fieldConfig.type === "dummy") {
                        continue;
                    }

                    // Convert string boolean values to actual booleans for checkboxes
                    let formValue = value;
                    if (fieldConfig && fieldConfig.type === "checkbox") {
                        formValue = value === "true" || value === true;
                    }
                    formValues[`${key}-name`] = formValue;
                }

                // Save state and set form values
                Object.entries(formValues).forEach(([key, value]) => {
                    setValue(key, value);
                });
                setDefaultValues(formValues);
                setFormConfig(ordered);
                setIsLoadingData(false);
            });
        }
    }, [patientId]);

    useEffect(() => {
        setFormConfig((prev) => {
            const newConfig = { ...prev };
            Object.entries(newConfig).forEach(([key, value]) => {
                newConfig[key].disabled = !isEditing;
            });
            return newConfig;
        });
    }, [isEditing]);

    return (
        <section>
            <h2 className="sr-only">Historia Clínica del paciente en MiFisio Agenda</h2>

            <h3 className="text-primary text-2xl">Historia Clínica</h3>

            <div className="my-3">
                {isLoadingData ? (
                    <CustomFormSkeleton className="grid grid-cols-1 md:grid-cols-2 gap-3" />
                ) : Object.keys(formConfig).length === 0 ? (
                    <EmptyTable
                        icon={faNewspaper}
                        title="La plantilla de historia clínica está vacía"
                        description="Añade campos a la plantilla de historia clínica para poder editarlos desde cada paciente."
                        showButton={true}
                        buttonText="Añadir campos"
                        onButtonClick={handleSubmitWithoutValidation(
                            navigate,
                            `/clinic/${activeClinic?.id}?tab=patient-history-template`,
                        )}
                    />
                ) : (
                    <CustomForm
                        formConfig={formConfig}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                        isLoading={isLoadingData}
                    />
                )}

                {(Object.keys(formConfig).length > 0 || isLoadingData) && (
                    <div className="mt-5 flex space-x-2 sm:space-x-4">
                        {!isEditing ? (
                            <>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSubmitWithoutValidation(setIsEditing, true)}
                                    disabled={isSubmitting}
                                >
                                    Editar
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSubmit(
                                        putPatientHistory,
                                        patientId,
                                        handleSubmitWithoutValidation(setIsEditing, false),
                                        setDefaultValues,
                                    )}
                                    disabled={isSubmitting}
                                >
                                    Guardar
                                </button>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSubmitWithoutValidation(cancelEditing)}
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default PatientHistory;
